import { StyleSheet, Platform } from 'react-native';
import { AdminColors } from '../../../../../Admin/constants/adminColors';
import FontFamily from '../../../../../../constants/fonts';

const isWeb = Platform.OS === 'web';

const styles = StyleSheet.create({
  // ── Loading / empty states ─────────────────────────────────────────────
  stateContainer: {
    padding: 28,
    alignItems: 'center',
    gap: 8,
  },
  stateText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textSecondary,
    textAlign: 'center',
  },

  // ── "No open coaching item" success banner ────────────────────────────
  noOpenItemBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: isWeb ? 16 : 10,
    paddingVertical: 16,
  },
  noOpenItemIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.green75,
  },
  noOpenItemTextWrap: {
    flex: 1,
    gap: 2,
  },
  noOpenItemTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: '#2D2D2D',
  },
  noOpenItemSubtitle: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: '#616161',
    lineHeight: 17,
  },

  // ── Inner table container ─────────────────────────────────────────────
  tableContainer: {
    // No overflow:'hidden' — it would clip the result picker dropdown below the table.
    borderWidth: 1,
    borderColor: AdminColors.gray25,
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
  },

  // ── Table header row ──────────────────────────────────────────────────
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#E8F0FB',
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderTable,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: isWeb ? 16 : 10,
  },

  // ── Row wrapper — raises zIndex above rows below it while its result picker is open ──
  rowWrapper: {
    zIndex: 1,
  },
  rowWrapperOpen: {
    zIndex: 1000,
    elevation: 24,
  },

  // ── Data row ──────────────────────────────────────────────────────────
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
    paddingVertical: 12,
    paddingHorizontal: isWeb ? 16 : 10,
    // Centered (not flex-start) so the result pill/dropdown's own internal padding
    // doesn't make it look lower than the plain-text cells beside it.
    alignItems: 'center',
  },
  // Raises this row above the follow-up feedback row below it (its sibling)
  // so the open result picker isn't painted under the textarea.
  tableRowOpen: {
    zIndex: 1000,
    elevation: 24,
  },

  // ── Follow-up feedback expansion row ─────────────────────────────────
  followUpRow: {
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
    paddingHorizontal: isWeb ? 16 : 10,
    paddingVertical: 10,
    backgroundColor: AdminColors.bgPage,
  },

  // ── Column flex widths (all relative, no fixed) ───────────────────────
  colSource:    { flex: 1.3, paddingRight: 8 },
  colFocus:     { flex: 1.4, paddingRight: 8 },
  colType:      { flex: 1,   paddingRight: 8 },
  colPlan:      { flex: 2.2, paddingRight: 8 },
  colDate:      { flex: 1,   paddingRight: 8, alignItems: isWeb ? 'flex-end' : 'flex-start' },
  colResult:    { flex: 1.8 },

  // Wraps the result pill/dropdown so it stretches to fill colResult's width.
  resultCellWrapper: {
    alignSelf: 'stretch',
  },

  // ── Column header text — uppercase small-caps like HTML ───────────────
  thText: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    color: AdminColors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // ── Source cell — blue clickable link style ───────────────────────────
  sourceText: {
    fontFamily: FontFamily.bold,
    fontSize: isWeb ? 13 : 12,
    color: AdminColors.blue700,           // blue link colour matching HTML
  },

  // ── Focus area — bold ────────────────────────────────────────────────
  focusText: {
    fontFamily: FontFamily.bold,
    fontSize: isWeb ? 13 : 12,
    color: AdminColors.textPrimary,
  },

  // ── Action plan ───────────────────────────────────────────────────────
  planText: {
    fontFamily: FontFamily.regular,
    fontSize: isWeb ? 13 : 12,
    color: AdminColors.textPrimary,
    lineHeight: 18,
  },
  supportText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textSecondary,
    marginTop: 3,
  },

  // ── Date ─────────────────────────────────────────────────────────────
  dateText: {
    fontFamily: FontFamily.regular,
    fontSize: isWeb ? 12 : 11,
    color: AdminColors.textSecondary,
  },

  // ── Category badge — Metric (light blue) / Process (light purple) ─────
  badge: {
    alignSelf: 'flex-start',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeMetric:  { backgroundColor: '#DBEAFE' },
  badgeProcess: { backgroundColor: '#EDE9FE' },
  badgeText: {
    fontFamily: FontFamily.medium,
    fontSize: 11,
  },
  badgeTextMetric:  { color: '#1D4ED8' },
  badgeTextProcess: { color: '#6D28D9' },

  // ── Follow-up result — pending dropdown / complete pill ───────────────
  // Pending: amber outlined — stretches to fill colResult so it lines up under the header.
  pendingDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    borderWidth: 1,
    borderColor: '#D97706',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: AdminColors.bgSurface,
    gap: 4,
  },
  pendingDropdownText: {
    fontFamily: FontFamily.regular,
    fontSize: isWeb ? 12 : 11,
    color: AdminColors.textPrimary,
    flex: 1,
  },
  // Complete: solid green pill
  completePill: {
    borderRadius: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#16A34A',
    alignSelf: 'flex-start',
  },
  completePillText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: '#FFFFFF',
  },
  // "Shows Improvement" / "No Improvement" pill — amber; stretches to match pendingDropdown.
  improvePill: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    gap: 4,
    borderRadius: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#D97706',
  },
  improvePillText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: '#FFFFFF',
  },
  // Clickable variant of the completePill (still-open items) — same green fill as the static pill.
  completePillButton: {
    backgroundColor: '#16A34A',
  },

  // ── Result picker option sheet ──────────────────────────────────────
  // Base look — positioning (top/left/width) is applied inline from measured
  // button coordinates when rendered inside the Modal (see pickerOverlayFloating).
  pickerOverlay: {
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 24,
  },
  pickerOverlayFloating: {
    position: 'absolute',
  },
  pickerOption: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  pickerOptionText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },

  // ── Follow-up feedback expansion ─────────────────────────────────────
  followUpLabel: {
    fontFamily: FontFamily.medium,
    fontSize: isWeb ? 13 : 12,
    color: AdminColors.textPrimary,
    marginBottom: 6,
  },
  followUpTextarea: {
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 4,
    padding: 8,
    backgroundColor: AdminColors.bgSurface,
    minHeight: 72,
    fontFamily: FontFamily.regular,
    fontSize: isWeb ? 13 : 12,
    color: AdminColors.textPrimary,
    textAlignVertical: 'top',
  },
  followUpTextareaError: { borderColor: AdminColors.textError },
  followUpHint: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textError,
    marginTop: 4,
  },

  // ── Show more / collapse ─────────────────────────────────────────────
  showMoreRow: {
    paddingVertical: 10,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderDefault,
  },
  showMoreText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.blue700,
  },
});

export default styles;
